/**
 * ImprovementRecommendation - Single Recommendation Card
 *
 * Displays a single category of improvement recommendations
 * with expandable details and severity indicator.
 *
 * @version 1.0.0
 * @date 2026-02-04
 */

import React, { useState } from 'react';

// =============================================================================
// TYPES
// =============================================================================

export type RecommendationSeverity = 'critical' | 'high' | 'medium' | 'low';

interface RecommendationItem {
  label: string;
  detail: string;
  confidence?: 'high' | 'medium' | 'low';
  rationale?: string;
}

interface ImprovementRecommendationProps {
  severity: RecommendationSeverity;
  title: string;
  description: string;
  items: RecommendationItem[];
  maxItems?: number;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

const getSeverityColor = (severity: RecommendationSeverity): string => {
  switch (severity) {
    case 'critical':
      return '#ef4444'; // Red
    case 'high':
      return '#f97316'; // Orange
    case 'medium':
      return '#f59e0b'; // Yellow
    case 'low':
      return '#10b981'; // Green
    default:
      return '#6b7280'; // Gray
  }
};

const getSeverityIcon = (severity: RecommendationSeverity): string => {
  switch (severity) {
    case 'critical':
      return '🔴';
    case 'high':
      return '🟠';
    case 'medium':
      return '🟡';
    case 'low':
      return '🟢';
    default:
      return '•';
  }
};

const getSeverityLabel = (severity: RecommendationSeverity): string => {
  return severity.toUpperCase();
};

const getConfidenceBadge = (confidence?: 'high' | 'medium' | 'low'): React.ReactNode => {
  if (!confidence) return null;

  const colors = {
    high: { bg: 'rgba(16, 185, 129, 0.2)', text: '#10b981' },
    medium: { bg: 'rgba(245, 158, 11, 0.2)', text: '#f59e0b' },
    low: { bg: 'rgba(107, 114, 128, 0.2)', text: '#9ca3af' },
  };

  const style = colors[confidence];

  return (
    <span
      className="confidence-badge"
      style={{
        backgroundColor: style.bg,
        color: style.text,
        padding: '2px 6px',
        borderRadius: '4px',
        fontSize: '0.7rem',
        fontWeight: 500,
        marginLeft: '8px',
      }}
    >
      {confidence.toUpperCase()}
    </span>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const ImprovementRecommendation: React.FC<ImprovementRecommendationProps> = ({
  severity,
  title,
  description,
  items,
  maxItems = 5,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const visibleItems = isExpanded ? items : items.slice(0, maxItems);
  const hasMore = items.length > maxItems;

  return (
    <div
      className="improvement-recommendation"
      style={{
        borderLeft: `4px solid ${getSeverityColor(severity)}`,
      }}
    >
      {/* Header */}
      <div className="recommendation-header">
        <span className="severity-icon">{getSeverityIcon(severity)}</span>
        <h4 className="recommendation-title">{title}</h4>
        <span
          className="severity-badge"
          style={{
            backgroundColor: getSeverityColor(severity),
            color: 'white',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '0.7rem',
            fontWeight: 600,
          }}
        >
          {getSeverityLabel(severity)}
        </span>
      </div>

      {/* Description */}
      <p className="recommendation-description">{description}</p>

      {/* Items List */}
      <div className="recommendation-items">
        {visibleItems.map((item, index) => (
          <div key={index} className="recommendation-item">
            <div className="item-header">
              <span className="item-label">• {item.label}</span>
              {getConfidenceBadge(item.confidence)}
            </div>
            <div className="item-detail">{item.detail}</div>
            {item.rationale && (
              <div className="item-rationale">
                <span className="rationale-label">Rationale:</span> {item.rationale}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Show More/Less */}
      {hasMore && (
        <button
          className="recommendation-toggle"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded
            ? 'Show Less'
            : `View All ${items.length} Suggestions →`}
        </button>
      )}
    </div>
  );
};

export default ImprovementRecommendation;
